import { useMemberPickerBottomSheetContext } from '@euler/app/components/MemberPickerBottomSheet';
import { IconProps } from '@euler/app/components/icons/types';
import { Typography } from '@euler/app/components/typography';
import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { kSubjectTypeInfoMap } from '@euler/app/flows/order/pages/ServiceSubjectsScreen/functions/subjectTypeInfoUtil';
import { serviceSubjectEquals } from '@euler/app/flows/order/util/serviceSubjectEquals';
import { sortedServiceSubjectsByType } from '@euler/app/flows/order/util/sortedServiceSubjectsByType';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { ErrorText } from '@euler/components/form/Input';
import { HStack } from '@euler/components/layout/Stack';
import { OrgUserRoleHelper } from '@euler/functions/OrgUserRoleHelper';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useAlert } from '@euler/functions/ux/useAlert';
import { getValidateErrorMessage } from '@euler/lib/form/controller';
import { ServiceSubject } from '@euler/model';
import { OrgMember } from '@euler/model/entity';
import { OrgUserRoleType, ServiceSubjectType } from '@euler/model/enum';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { memo, useCallback } from 'react';
import {
  StyleProp,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { Path, Svg } from 'react-native-svg';
import { useOrderFlowContext } from '../../../../OrderFlowContext';
import { Cell } from '../components/Cell';
import { IconWrapper } from '../components/IconWrapper';
import { Row } from '../components/Row';

export const ServiceSubjectsRow = memo(
  ({ required, ...props }: { required?: boolean; disabled?: boolean }) => {
    const alert = useAlert();
    const {
      theme,
      navigation,
      orderDetail$,
      updateDetail,
      formController: { form },
      storeConfiguration,
    } = useOrderFlowContext();
    const { iconColor, placeholderColor } = theme;
    const { rpx } = useResponsiveContext();
    const [orderDetail] = useBehaviorSubject(orderDetail$);
    const currentUser = useCurrentUserDangerously();

    const onPress = usePersistFn(() => {
      navigation.navigate('_serviceSubjects', {});
    });

    const onRemoveSubject = usePersistFn((subject: ServiceSubject) => {
      if (!orderDetail.serviceSubjects) return;
      const index = orderDetail.serviceSubjects.findIndex(x =>
        serviceSubjectEquals(x, subject),
      );
      if (index >= 0) {
        updateDetail(draft => {
          draft.serviceSubjects?.splice(index, 1);
        });
        form.serviceSubjects.validate();
      }
    });

    const onAssigned = useCallback(
      (subject: ServiceSubject, member: OrgMember) => {
        const roles =
          subject.type === ServiceSubjectType.Construction
            ? storeConfiguration.restrictedRolesForUpdateConstructionJobAssignment
            : subject.type === ServiceSubjectType.Inspection
            ? storeConfiguration.restrictedRolesForUpdateInspectionFlowAssignment
            : undefined;
        if (member.id != subject.assignedTo?.id && roles?.length) {
          const roleHelper = new OrgUserRoleHelper(currentUser.user.role);
          if (!roleHelper.matchesAny(roles)) {
            void alert({
              title: '您没有权限修改该项目派工',
              message: '若需帮助，请联系门店系统管理员！',
            });
            return;
          }
        }

        updateDetail(draft => {
          if (!draft.serviceSubjects) return;

          const index = draft.serviceSubjects.findIndex(x =>
            serviceSubjectEquals(x, subject),
          );

          if (
            index >= 0 &&
            draft.serviceSubjects[index].assignedTo?.id !== member.id &&
            draft.serviceSubjects[index].assignedTo?.name !== member.name
          ) {
            draft.serviceSubjects[index].assignedTo = {
              id: member.id,
              name: member.name,
            };
          }
        });
        form.serviceSubjects.validate();
      },
      [
        alert,
        currentUser.user.role,
        form.serviceSubjects,
        storeConfiguration.restrictedRolesForUpdateConstructionJobAssignment,
        storeConfiguration.restrictedRolesForUpdateInspectionFlowAssignment,
        updateDetail,
      ],
    );

    return (
      <Row
        errorProp="serviceSubjects"
        icon={
          <IconWrapper>
            <RowIcon color={iconColor} />
          </IconWrapper>
        }
        detailIcon="disclosure"
        onPress={onPress}
        extra={
          <ServiceSubjectList
            subjects={orderDetail.serviceSubjects}
            editable={!props.disabled}
            onRemoveSubject={onRemoveSubject}
            onAssigned={onAssigned}
          />
        }
        {...props}
      >
        <Cell label="服务项目" required={required}>
          <Typography.Label
            color={
              orderDetail.serviceSubjects?.length
                ? StatusColors.Success
                : placeholderColor
            }
            size={rpx(16)}
            family={orderDetail.serviceSubjects?.length ? 'medium' : 'light'}
          >
            包含{orderDetail.serviceSubjects?.length ?? 0}个服务项目
          </Typography.Label>
          {form.serviceSubjects.error ? (
            <ErrorText
              errorMsg={getValidateErrorMessage(form.serviceSubjects.error)}
              css={`
                margin-top: ${rpx(8)}px;
              `}
            />
          ) : null}
        </Cell>
      </Row>
    );
  },
);

const ServiceSubjectList = memo(
  ({
    subjects,
    editable = true,
    onRemoveSubject,
    onAssigned,
  }: {
    subjects?: ServiceSubject[];
    editable?: boolean;
    onRemoveSubject?: (subject: ServiceSubject) => void;
    onAssigned?: (subject: ServiceSubject, member: OrgMember) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    if (!subjects?.length) return null;
    return (
      <View
        css={`
          margin-top: ${rpx(15)}px;
        `}
      >
        {sortedServiceSubjectsByType(subjects).map((subject, index) => (
          <ServiceSubjectCell
            index={index}
            key={`${subject.type}:${subject.name}`}
            subject={subject}
            editable={editable}
            canAssign={true}
            onRemove={onRemoveSubject}
            onAssigned={onAssigned}
          />
        ))}
      </View>
    );
  },
);

const kShowRemoveButton = false;

export const ServiceSubjectCell = memo(
  ({
    index,
    subject,
    editable = true,
    style,
    nameStyle,
    labelStyle,
    canAssign,
    onRemove,
    onAssigned,
  }: {
    index: number;
    subject: ServiceSubject;
    editable?: boolean;
    style?: StyleProp<ViewStyle>;
    nameStyle?: StyleProp<TextStyle>;
    labelStyle?: StyleProp<TextStyle>;
    canAssign?: boolean;
    onRemove?: (subject: ServiceSubject) => void;
    onAssigned?: (subject: ServiceSubject, member: OrgMember) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const subjectTypeInfo = kSubjectTypeInfoMap[subject.type];
    const context = useMemberPickerBottomSheetContext();

    const onPress = useCallback(() => {
      onRemove?.(subject);
    }, [onRemove, subject]);

    const onAssignPress = useCallback(() => {
      context?.memberPickerPresentationRequest$.next({
        title: '项目派工',
        role: OrgUserRoleType.Technicians,
        onSelect: member => {
          context?.memberPickerPresentationRequest$.next(undefined);
          onAssigned?.(subject, member);
        },
      });
    }, [context?.memberPickerPresentationRequest$, onAssigned, subject]);

    return (
      <HStack
        justifyContent="space-between"
        alignItems="center"
        css={`
          min-height: ${rpx(36)}px;
          border-top-width: 1px;
          border-top-color: rgb(245, 245, 245);
          margin-left: ${rpx(10)}px;
          padding-top: ${rpx(4)}px;
          padding-bottom: ${rpx(4)}px;
        `}
        style={style}
      >
        <HStack
          justifyContent="flex-start"
          css={`
            flex: 1;
            margin-right: ${rpx(8)}px;
          `}
        >
          <Typography.Label
            size={rpx(14)}
            regular
            color="#333"
            numberOfLines={0}
            style={nameStyle}
            css={`
              flex: 1;
            `}
          >
            {`${index + 1}. ${subject.name}`}
          </Typography.Label>
          {canAssign ? (
            <Center.Touchable
              onPress={onAssignPress}
              css={`
                flex-direction: row;
                align-items: center;
                background-color: ${subject.assignedTo?.name
                  ? StatusColors.Info
                  : StatusColors.Danger};
                border-radius: ${rpx(3)}px;
                padding: 0 ${rpx(4)}px;
              `}
            >
              <Typography.Text
                size={rpx(11)}
                lineHeight={rpx(18)}
                color="white"
              >
                {subject.assignedTo?.name || '⚠ 派工'}
              </Typography.Text>
              <Icon
                name="pencil"
                size={rpx(8)}
                color="white"
                style={{
                  marginLeft: rpx(3),
                }}
              />
            </Center.Touchable>
          ) : (
            <Typography.Label
              size={rpx(12)}
              regular
              color={subject.assignedTo ? '#aaa' : '#c37e5c'}
            >
              {subject.assignedTo ? `(${subject.assignedTo.name})` : '⚠'}
            </Typography.Label>
          )}
        </HStack>
        <HStack
          justifyContent="flex-end"
          css={`
            flex-shrink: 0;
            flex-grow: 0;
          `}
        >
          <Center
            css={`
              border-radius: ${rpx(3)}px;
              height: ${rpx(20)}px;
              padding: 0 ${rpx(5)}px;
              background-color: ${subjectTypeInfo.bgColor};
              margin-left: ${rpx(5)}px;
            `}
          >
            <Typography.Label
              color={subjectTypeInfo.textColor}
              light
              size={rpx(10)}
              lineHeight={rpx(20)}
              style={labelStyle}
            >
              {subjectTypeInfo.text}
            </Typography.Label>
          </Center>
          {kShowRemoveButton && onRemove != null && editable && (
            <TouchableOpacity
              onPress={onPress}
              css={`
                width: ${rpx(50)}px;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
              `}
              hitSlop={PredefinedHitSlops.large}
            >
              <Icon
                name={'minus-circle'}
                color={StatusColors.Danger}
                size={rpx(24)}
              />
            </TouchableOpacity>
          )}
        </HStack>
      </HStack>
    );
  },
);

const RowIcon = memo(({ width, height, color, style, ...props }: IconProps) => {
  const { rpx } = useResponsiveContext();
  return (
    <Svg
      viewBox="0 0 16.2513 21"
      width={width ?? rpx(16)}
      height={height ?? rpx(21)}
      style={style}
      fill="none"
      {...props}
    >
      <Path
        d="M8.64563 1.03714C8.51282 0.904388 8.32751 0.821411 8.12292 0.821411C7.91821 0.821411 7.73572 0.904388 7.60022 1.03714C7.46741 1.16989 7.3844 1.35519 7.3844 1.55986C7.3844 1.76452 7.46741 1.94982 7.60022 2.08257C7.72461 2.20703 7.89612 2.28724 8.08411 2.2983L8.15881 2.2983C8.34692 2.28724 8.51843 2.20703 8.64282 2.08257C8.77563 1.94982 8.85852 1.76452 8.85852 1.55986C8.86133 1.35519 8.78113 1.16989 8.64563 1.03714ZM8.12292 0C8.59863 0 9.03003 0.0276566 9.33984 0.340179C9.65234 0.65271 9.84314 1.08139 9.84314 1.55986L11.8124 1.55986L11.8124 4.02133L4.42786 4.02133L4.42786 1.55986L6.39709 1.55986C6.39709 1.08416 6.5907 0.65271 6.9032 0.342949C7.21851 0.030426 7.64722 0 8.12292 0ZM1.64001 2.2983L3.77795 2.2983L3.77795 3.28289L1.64001 3.28289C1.46033 3.28289 1.29712 3.35757 1.17822 3.47649C1.05933 3.59541 0.984619 3.75859 0.984619 3.94113L0.984619 19.3655C0.984619 19.725 1.28052 20.0209 1.64001 20.0209L14.6112 20.0209C14.9708 20.0209 15.2667 19.725 15.2667 19.3655L15.2667 3.93836C15.2667 3.75859 15.192 3.59541 15.0731 3.47372C14.9542 3.3548 14.791 3.28013 14.6112 3.28013L12.4734 3.28013L12.4734 2.29553L14.6112 2.29553C15.062 2.29553 15.4741 2.48083 15.77 2.77677C16.066 3.0727 16.2513 3.48479 16.2513 3.9356L16.2513 19.3599C16.2513 20.2616 15.5128 21 14.6112 21L1.64001 21C0.735718 21 0 20.2616 0 19.3599L0 3.93836C0 3.48756 0.185303 3.07546 0.481201 2.77953C0.777222 2.48083 1.18652 2.2983 1.64001 2.2983Z"
        fillRule="evenodd"
        fill={color}
      />
      <Path
        d="M3.19385 16.6689L12.7107 16.6689L12.7107 17.3271L3.19385 17.3271L3.19385 16.6689ZM3.19385 10.5982L4.69568 10.5982L5.66638 8.18371C5.73279 8.015 5.92358 7.93479 6.09229 8.00117C6.18079 8.03712 6.24438 8.10627 6.27759 8.18924L7.73511 12.0004L9.04602 6.96956C9.09033 6.79532 9.27014 6.69022 9.44434 6.73447C9.54663 6.75937 9.62415 6.83405 9.66284 6.92255L11.2034 10.5152L12.7107 10.5152L12.7107 11.1707L10.9875 11.1707C10.8604 11.1707 10.7386 11.096 10.6862 10.9715L9.43604 8.05925L8.11682 13.1288C8.09741 13.2366 8.02283 13.3335 7.91211 13.3749C7.74341 13.4385 7.55261 13.3556 7.48901 13.1841L5.96228 9.19872L5.22937 11.0241C5.18787 11.1568 5.06348 11.2536 4.91687 11.2536L3.19385 11.2536L3.19385 10.5982ZM3.19385 14.9459L12.7107 14.9459L12.7107 15.6013L3.19385 15.6013L3.19385 14.9459Z"
        fillRule="evenodd"
        fill={color}
      />
    </Svg>
  );
});
